// Simple matches list. Tier rings are cut from v1; this just shows recent
// chats with the last persona message preview. Starred chats are pulled into
// a Favorites section pinned above the rest. Story rings for curated matches
// live above the list — tapping a ring opens that persona's daily stories.

import { useMemo, useState } from 'react';
import { GraduationCap, Heart, Plus, Star } from 'lucide-react';
import type { ChatMessage, ChatState, Persona, Relationship, Story } from '@/lib/types';
import { PERSONAS } from '@/data/personas';
import { storiesFor, STORIES_ENABLED } from '@/data/stories';
import { Portrait } from '@/components/portraits';
import { PersonaProfileSheet } from '@/components/PersonaProfileSheet';
import { ScreenBackdrop } from '@/components/visual/ScreenBackdrop';
import { StoryRing } from '@/features/stories/StoryRing';
import { C } from '@/styles/tokens';

interface MatchesScreenProps {
  matches: Persona[];
  chatStates: Record<string, ChatState>;
  favorites: Record<string, true>;
  relationships: Record<string, Relationship>;
  /** Map of personaId → the story-batch date last viewed. */
  viewedStories: Record<string, string>;
  /** Current story-batch date (UTC YYYY-MM-DD). Used to decide which rings
   *  should appear watched vs unwatched today, Instagram-style. */
  storyDate: string;
  dailyStories: Record<string, Story[]> | null;
  onOpenChat: (personaId: string) => void;
  onOpenStories: (personaId: string) => void;
  onToggleFavorite: (personaId: string) => void;
  /** Opens the HSKBuilder modal from the Chats list header. */
  onCreateCharacter: () => void;
  /** Jumps to the Discover tab — surfaced by the empty-state CTA. */
  onGoToDiscover: () => void;
}

function lastPersonaMessage(history: ChatMessage[]): ChatMessage | undefined {
  for (let i = history.length - 1; i >= 0; i--) {
    const m = history[i]!;
    if (m.from === 'them') return m;
  }
  return undefined;
}

export function MatchesScreen({
  matches,
  chatStates,
  favorites,
  relationships,
  viewedStories,
  storyDate,
  dailyStories,
  onOpenChat,
  onOpenStories,
  onToggleFavorite,
  onCreateCharacter,
  onGoToDiscover,
}: MatchesScreenProps) {
  const byRecency = (a: Persona, b: Persona) => {
    const aTs = chatStates[a.id]?.history.at(-1)?.ts ?? 0;
    const bTs = chatStates[b.id]?.history.at(-1)?.ts ?? 0;
    return bTs - aTs;
  };
  const sorted = matches.slice().sort(byRecency);
  const favorited = sorted.filter((p) => favorites[p.id]);
  const others = sorted.filter((p) => !favorites[p.id]);
  const [profilePersona, setProfilePersona] = useState<Persona | null>(null);

  // Rings row covers curated matches only. Custom personas don't have
  // generated stories so they'd render as inert greyed-out rings.
  const ringedPersonas = useMemo(
    () => PERSONAS.filter((p) => relationships[p.id]?.matched),
    [relationships],
  );

  return (
    <section className="relative h-full flex flex-col overflow-hidden" style={{ background: C.cream }}>
      <ScreenBackdrop mark="聊" position="tr" size={260} rotate={-10} />
      <div
        className="flex items-center gap-3 px-4 py-3 relative"
        style={{ borderBottom: `1px solid ${C.blush}` }}
      >
        <h1 className="ff-display text-2xl flex-1 flex items-baseline gap-2" style={{ color: C.ink }}>
          Chats
          <span className="ff-zh-serif text-base opacity-50">· 聊天</span>
        </h1>
        <button
          type="button"
          onClick={onCreateCharacter}
          className="px-3 py-1.5 rounded-full ff-body text-[10px] uppercase tracking-[0.2em] font-bold inline-flex items-center gap-1.5"
          aria-label="Customize a new character"
          style={{ background: C.vermillion, color: 'white' }}
        >
          <Plus size={12} aria-hidden strokeWidth={2.75} />
          Customize
          <span className="ff-zh-serif text-[11px] tracking-normal opacity-80">定制</span>
        </button>
      </div>

      {STORIES_ENABLED && ringedPersonas.length > 0 && (
        <div className="px-4 pt-3 overflow-x-auto no-scrollbar" aria-label="Story rings">
          <ul className="flex gap-3 pb-2">
            {ringedPersonas.map((p) => (
              <li key={p.id}>
                <StoryRing
                  persona={p}
                  status={relationships[p.id] ?? null}
                  hasStory={storiesFor(p.id, dailyStories).length > 0}
                  // Watched ring fades only when the user has viewed *today's*
                  // batch. A fresh storyDate resets every ring to vermillion.
                  viewed={Boolean(storyDate) && viewedStories[p.id] === storyDate}
                  onClick={() => onOpenStories(p.id)}
                />
              </li>
            ))}
          </ul>
        </div>
      )}

      {sorted.length === 0 && (
        <div className="flex flex-col items-center justify-center text-center py-12 px-6">
          <div className="ff-zh-serif text-7xl mb-4" style={{ color: C.gold, opacity: 0.5 }} aria-hidden>
            聊
          </div>
          <div className="ff-display italic text-2xl mb-1" style={{ color: C.ink }}>
            No chats yet
          </div>
          <div className="ff-zh-serif text-sm mb-2" style={{ color: C.inkLight }}>
            还没有聊天
          </div>
          <div
            className="ff-body text-[12px] max-w-[260px] leading-relaxed"
            style={{ color: C.inkLight }}
          >
            Swipe right on a persona in Discover, or build a character in Customize Mode, to start a
            conversation.
          </div>
          <button
            type="button"
            onClick={onGoToDiscover}
            className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full ff-body text-[13px] font-bold"
            style={{ background: C.vermillion, color: 'white', boxShadow: '0 4px 14px rgba(233,75,60,0.30)' }}
            aria-label="Go to Discover to find your HanZi friend"
          >
            <Heart size={14} aria-hidden strokeWidth={2.4} />
            Find your HanZi friend
            <span className="ff-zh-serif text-[12px] tracking-normal opacity-90">· 发现</span>
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {favorited.length > 0 && (
          <Section label="Favorites" hz="喜欢">
            {favorited.map((p) => (
              <ChatCard
                key={p.id}
                persona={p}
                last={lastPersonaMessage(chatStates[p.id]?.history ?? [])}
                isFavorite
                onOpen={() => onOpenChat(p.id)}
                onOpenProfile={() => setProfilePersona(p)}
                onToggleFavorite={() => onToggleFavorite(p.id)}
              />
            ))}
          </Section>
        )}
        {favorited.length > 0 && others.length > 0 && (
          <div className="brush-divider-h mx-6 -my-1" aria-hidden />
        )}
        {others.length > 0 && (
          <Section
            label={favorited.length > 0 ? 'All chats' : undefined}
            hz={favorited.length > 0 ? '全部聊天' : undefined}
          >
            {others.map((p) => (
              <ChatCard
                key={p.id}
                persona={p}
                last={lastPersonaMessage(chatStates[p.id]?.history ?? [])}
                isFavorite={false}
                onOpen={() => onOpenChat(p.id)}
                onOpenProfile={() => setProfilePersona(p)}
                onToggleFavorite={() => onToggleFavorite(p.id)}
              />
            ))}
          </Section>
        )}
      </div>

      {profilePersona && (
        <PersonaProfileSheet
          persona={profilePersona}
          onClose={() => setProfilePersona(null)}
        />
      )}
    </section>
  );
}

function Section({
  label,
  hz,
  children,
}: {
  label?: string;
  hz?: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      {label && (
        <h2
          className="ff-body text-[10px] uppercase tracking-[0.3em] mb-2 px-1 flex items-baseline gap-2"
          style={{ color: C.inkLight }}
        >
          {label}
          {hz && (
            <span className="ff-zh-serif text-[12px] tracking-normal opacity-80">
              · {hz}
            </span>
          )}
        </h2>
      )}
      <ul className="space-y-2">{children}</ul>
    </section>
  );
}

function ChatCard({
  persona,
  last,
  isFavorite,
  onOpen,
  onOpenProfile,
  onToggleFavorite,
}: {
  persona: Persona;
  last: ChatMessage | undefined;
  isFavorite: boolean;
  onOpen: () => void;
  onOpenProfile: () => void;
  onToggleFavorite: () => void;
}) {
  // Three sibling buttons: portrait (opens profile), main row (opens chat),
  // and star. Sibling buttons (rather than nested) keep the markup valid and
  // let each have its own focus ring + screen-reader label.
  return (
    <li
      className="relative flex items-center rounded-2xl overflow-hidden shadow-paper"
      style={{ background: C.cream, border: `1px solid ${C.mist}` }}
    >
      <button
        type="button"
        onClick={onOpenProfile}
        className="p-3 flex-shrink-0"
        aria-label={`View ${persona.name}'s profile`}
      >
        <span className="block w-12 h-12 rounded-full overflow-hidden" aria-hidden>
          <Portrait persona={persona} />
        </span>
      </button>
      <button
        type="button"
        onClick={onOpen}
        className="flex-1 min-w-0 py-3 pr-3 text-left"
        aria-label={`Open chat with ${persona.name}, ${persona.level.tag}`}
      >
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className="ff-zh-serif text-lg font-bold leading-none truncate">{persona.name}</span>
          <span
            className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold tracking-wider"
            style={{ background: C.blush, color: C.vermillionDeep }}
          >
            <GraduationCap size={9} aria-hidden />
            {persona.level.tag}
          </span>
        </div>
        <div className="ff-body text-[12px] truncate" style={{ color: C.inkLight }}>
          {last ? last.hz : `New match · ${persona.intro}`}
        </div>
      </button>
      <button
        type="button"
        onClick={onToggleFavorite}
        className="px-4 py-3 flex items-center justify-center flex-shrink-0"
        aria-label={
          isFavorite ? `Unstar chat with ${persona.name}` : `Star chat with ${persona.name}`
        }
        aria-pressed={isFavorite}
      >
        <Star
          size={18}
          aria-hidden
          fill={isFavorite ? C.gold : 'none'}
          color={isFavorite ? C.gold : C.inkSofter}
        />
      </button>
    </li>
  );
}
